import type { Image } from '@frontend/libs/types/media'
import type { Product } from '@frontend/libs/types/product'

import { create } from 'zustand'

import { type filters, type LookBook, type Pagination, Sort } from './type'

export type SelectedFilters = {
  brands: string[]
  categories: string[]
  collections: string[]
  colors: string[]
  discounts: string[]
  search: string
  sizes: string[]
  sort: Sort
}

export type ProductListState = {
  content: null | string
  featuredImage: Image | null
  featuredType: 'image' | 'video'
  featuredVideo: null | string
  filters: filters
  limit: number
  lookBooks: LookBook[]
  page: number
  pagination: Pagination
  products: Product[]
  selectedFilters: SelectedFilters
}

export type ProductListAction = {
  clearFilter: () => void
  init: () => void
  reset: () => void
  setContent: (content: string) => void
  setFeatured: (
    featuredImage: Image | null,
    featuredVideo: null | string,
    featuredType: 'image' | 'video',
  ) => void
  setFilters: (filters: filters) => void
  setLimit: (limit: number) => void
  setLookBooks: (lookBooks: LookBook[]) => void
  setPage: (page: number) => void
  setPagination: (pagination: Pagination) => void
  setProducts: (products: Product[]) => void
  setSelectedFilters: (
    selectedFilters: ProductListState['selectedFilters'],
  ) => void
  setSort: (sort: Sort) => void
  updateSelectedFilters: (value: string[], key: string) => void
}

const initialState: ProductListState = {
  content: null,
  featuredImage: null,
  featuredType: 'image',
  featuredVideo: null,
  filters: [],
  limit: 12,
  lookBooks: [],
  page: 1,
  pagination: {
    limit: 0,
    page: 1,
    pageCount: 0,
    total: 0,
  },
  products: [],
  selectedFilters: {
    brands: [],
    categories: [],
    collections: [],
    colors: [],
    discounts: [],
    search: '',
    sizes: [],
    sort: Sort.Default,
  },
}

export const useProductListStore = create<ProductListAction & ProductListState>(
  (set, get) => {
    return {
      ...initialState,
      clearFilter: () => {
        set(() => {
          return { selectedFilters: { ...initialState.selectedFilters } }
        })
        get().init()
      },
      init: () => set((state) => ({ ...state, page: 1, products: [] })),
      reset: () => set({ ...initialState }),
      setContent: (content) => set({ content }),
      setFeatured: (featuredImage, featuredVideo, featuredType) => {
        set({ featuredImage, featuredType, featuredVideo })
      },
      setFilters: (filters) => set({ filters }),
      setLimit: (limit) => set({ limit }),
      setLookBooks: (lookBooks) => set({ lookBooks }),
      setPage: (page) => set({ page }),
      setPagination: (pagination) => set({ pagination }),
      setProducts: (products) => {
        if (get().page === 1) {
          set({ products })
        } else {
          set((state) => {
            return { products: [...state.products, ...products] }
          })
        }
      },
      setSelectedFilters: (selectedFilters) => set({ selectedFilters }),
      setSort: (sort) =>
        set((state) => {
          return { selectedFilters: { ...state.selectedFilters, sort } }
        }),
      updateSelectedFilters: (value, key) => {
        set((state) => {
          return { selectedFilters: { ...state.selectedFilters, [key]: value } }
        })
      },
    }
  },
)

export default useProductListStore
